import React, { useEffect, useRef, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { text as textStyles } from 'theme'
import Collapse from 'blocks.simple/collapse/collapse'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { grid } from '../../../theme'
import { updateUserSettings } from 'blocks.app/user/user.state'
import styles from '../settings.jcss'
import EditText from 'blocks.simple/editText/editText'
import { SwitchButton } from 'atoms/SwitchButton/SwitchButton-view'
import Dropdown from 'blocks.simple/dropdown/dropdown'

const frameFrequencyValues = [
    { id: 25, name: 25 },
    { id: 30, name: 30 },
    { id: 60, name: 60 },
]
const bitRate = [
    { id: 1000, name: '360p' },
    { id: 2500, name: '480p' },
    { id: 5000, name: '720p' },
    { id: 8000, name: '1080p' },
    { id: 16000, name: '1440p' },
    { id: 40000, name: '2160p' },
]

export const presetsOptions = [
    {
        id: 0,
        name: translate('configureManually')
    },
    {
        id: 1,
        name: translate('optimizedForAndroid4.4')
    },
    {
        id: 2,
        name: translate('optimizedForAndroid4.1')
    },
]

export const videoCodecOptions = [
    {
        id: 'libx264',
        name: 'libx264'
    },
]

export const audioCodecOptions = [
    {
        id: 'aac',
        name: 'aac'
    },
]

export const codecProfileOptions = [
    {
        id: 'baseline',
        name: 'baseline'
    }, 
    {
        id:'main',
        name: 'main'
    },
    {
        id: 'high',
        name: 'high'
    }
]

const codecLevelOptions: {id: number, name: number}[] = [];

for (let major = 1; major <= 5; major++) {
    for (let minor = 0; minor <= 9; minor++) {
        if (major === 5 && minor > 1) break; // до 5.1 включительно
        const level = parseFloat(`${major}.${minor}`);
        codecLevelOptions.push({
            id: level,
            name: level
        });
    }
}


export const codecPresetOptions = [
    {
        id: 'ultrafast',
        name: 'ultrafast'
    },
    {
        id: 'superfast',
        name: 'superfast'
    },
    {
        id: 'veryfast',
        name: 'veryfast'
    },
    {
        id: 'faster',
        name: 'faster'
    },
    {
        id: 'fast',
        name: 'fast'
    },
    {
        id: 'medium',
        name: 'medium'
    },
    {
        id: 'slow',
        name: 'slow'
    },
    {
        id: 'veryslow',
        name: 'veryslow'
    },
    {
        id: 'placebo',
        name: 'placebo'
    }
]

const codecCrfOptions: {id: number, name: number}[] = []

for (let i = 0;  i <= 51; i++) {
    codecCrfOptions.push({
        id: i,
        name: i
    })
}

const DownloadVideoOptions = () => {
    const dispatch = useDispatch()
    const settingsVideoConversionFps: any = useSelector<RootStateOrAny>(
        (state) => state.user.data.settings.videoConversion.fps
    )
    const settingsVideoConversionBitrate: any = useSelector<RootStateOrAny>(
        (state) => state.user.data.settings.videoConversion.bitrate
    )
    const settingsVideoConversionRemoveAudio: any = useSelector<RootStateOrAny>(
        (state) => state.user.data.settings.videoConversion.removeAudio
    )
    const settingsVideoConversionTrim: any = useSelector<RootStateOrAny>(
        (state) => state.user.data.settings.videoConversion.trim
    )
    const settingsVideoConversionDuration: any = useSelector<RootStateOrAny>(
        (state) => state.user.data.settings.videoConversion.duration
    )

    const settingsVideoConversion: any = useSelector<RootStateOrAny>(
        state => state.user.data.settings.videoConversion
    )

    const [stateVideoConversionFps, setVideoConversionFps] = useState<number>(settingsVideoConversionFps)
    const [stateVideoConversionBitrate, setVideoConversionBitrate] = useState<any>({
        id: settingsVideoConversionBitrate,
    })
    const [stateVideoConversionDuration, setVideoConversionDuration] = useState<number | null>(
        settingsVideoConversionDuration
    )

    const [usePreset, setUsePreset] = useState<boolean>(settingsVideoConversion.presetType !== undefined && settingsVideoConversion.presetType !== null)

    const onChangeFps = (fps: number): void => {
        dispatch(updateUserSettings({ data: { videoConversion: { fps: fps } }, isSaveSettings: true }))
    }

    const onChangeBitrate = (bitrate: number): void => {
        dispatch(updateUserSettings({ data: { videoConversion: { bitrate: bitrate } }, isSaveSettings: true }))
    }

    const onChangeDuration = (duration: number | null): void => {
        setVideoConversionDuration(duration)
        dispatch(updateUserSettings({ data: { videoConversion: { duration: duration } }, isSaveSettings: true }))
    }

    const onChangeRemoveAudio = (): void => {
        settingsVideoConversionRemoveAudio
            ? dispatch(updateUserSettings({ data: { videoConversion: { removeAudio: false } }, isSaveSettings: true }))
            : dispatch(updateUserSettings({ data: { videoConversion: { removeAudio: true } }, isSaveSettings: true }))
    }

    const onChangeTrim = (): void => {
        settingsVideoConversionTrim
            ? dispatch(updateUserSettings({ data: { videoConversion: { trim: false } }, isSaveSettings: true }))
            : dispatch(updateUserSettings({ data: { videoConversion: { trim: true } }, isSaveSettings: true }))
    }

    const usePrevious = (value: any) => {
        const ref = useRef()
        useEffect(() => {
            ref.current = value
        })
        return ref.current
    }

    let prevStateVideoConversionFps: any = usePrevious(stateVideoConversionFps)
    let prevStateVideoConversionBitrate: any = usePrevious(stateVideoConversionBitrate)

    useEffect(() => {
        stateVideoConversionFps &&
            prevStateVideoConversionFps !== stateVideoConversionFps &&
            onChangeFps(stateVideoConversionFps)
        stateVideoConversionBitrate &&
            prevStateVideoConversionBitrate !== stateVideoConversionBitrate &&
            onChangeBitrate(stateVideoConversionBitrate.id)
    }, [stateVideoConversionFps, stateVideoConversionBitrate])

    return (
        <Collapse title={() => translate('downloadVideoOptions')} className={cn(styles.background, grid.mb_md)}>
            <div className={cn(grid.col, grid.pt_micro)}>
                <Dropdown
                    containerClassName={cn(textStyles.center, styles.inputWidth, grid.pb_mini)}
                    centered
                    label={translate('frameFrequency')}
                    value={stateVideoConversionFps}
                    options={frameFrequencyValues}
                    onChange={(value) => setVideoConversionFps(value.name)}
                />
                <Dropdown
                    containerClassName={cn(textStyles.center, styles.inputWidth, grid.pb_mini)}
                    label={translate('availableBitRate')}
                    value={stateVideoConversionBitrate.id}
                    options={bitRate}
                    centered
                    onChange={(value) => setVideoConversionBitrate(value)}
                />
                <EditText
                    value={stateVideoConversionBitrate.id}
                    label={translate('customBitRate')}
                    className={cn(textStyles.center, styles.inputWidth, grid.pb_mini, styles.greyText)}
                    type={'number'}
                    disabled
                />
                <div>
                    <div className={cn(grid.pb_mini, grid.pt_md, grid.row, grid.justify, styles.inputWidth)}>
                        <div>{translate('videoDuration')}</div>
                        <SwitchButton
                            onChange={() =>
                                stateVideoConversionDuration !== null ? onChangeDuration(null) : onChangeDuration(30)
                            }
                            checked={stateVideoConversionDuration ? true : false}
                        />
                    </div>
                    <EditText
                        disabled={stateVideoConversionDuration ? false : true}
                        tabindex={'0'}
                        className={cn(textStyles.center, styles.overflowAuto, styles.inputWidth)}
                        value={stateVideoConversionDuration ? stateVideoConversionDuration : ''}
                        indentSize={'mini'}
                        type={'number'}
                        mod="withLightBorder"
                        onChange={(value: number) => setVideoConversionDuration(value)}
                        onBlur={() =>
                            !stateVideoConversionDuration
                                ? onChangeDuration(null)
                                : onChangeDuration(stateVideoConversionDuration)
                        }
                    />
                    <div className={cn(grid.pb_mini, grid.pt_md, grid.row, grid.justify, styles.inputWidth)}>
                        <span className={styles.spanWidth}>{translate('trimVideoOption')}</span>
                        <div>
                            <SwitchButton onChange={onChangeTrim} checked={settingsVideoConversionTrim} />
                        </div>
                    </div>
                    <div className={cn(grid.pb_mini, grid.pt_md, grid.row, grid.justify, styles.inputWidth)}>
                        {translate('removeAudio')}
                        <div>
                            <SwitchButton onChange={onChangeRemoveAudio} checked={settingsVideoConversionRemoveAudio} />
                        </div>
                    </div>
                </div>
                <div className={cn(grid.pb_mini, grid.pt_md, grid.row, grid.justify, styles.inputWidth)}>
                    <div>{translate('presets')}</div>
                    <SwitchButton
                        onChange={() => {
                            setUsePreset(!usePreset)
                        }}
                        checked={usePreset}
                    />
                </div>
                {usePreset && (<div>
                    <Dropdown
                        containerClassName={cn(textStyles.center, styles.inputWidth_L, grid.pb_mini)}
                        value={settingsVideoConversion.presetType}
                        options={presetsOptions}
                        centered
                        onChange={(value) => {
                            dispatch(updateUserSettings({ data: { videoConversion: { presetType: value.id } }, isSaveSettings: true }))
                        }}
                        dropDirection={'up'}
                    />
                    {settingsVideoConversion.presetType === 0 && (<div>
                        <Dropdown
                            containerClassName={cn(textStyles.center, styles.inputWidth, grid.pb_mini)}
                            label={translate('videoCodec')}
                            value={settingsVideoConversion.videoCodec}
                            options={videoCodecOptions}
                            centered
                            onChange={(value) => {
                                dispatch(updateUserSettings({ data: { videoConversion: { videoCodec: value.name } }, isSaveSettings: true }))
                            }}
                        />
                        <Dropdown
                            containerClassName={cn(textStyles.center, styles.inputWidth, grid.pb_mini)}
                            label={translate('audioCodec')}
                            value={settingsVideoConversion.audioCodec}
                            options={audioCodecOptions}
                            centered
                            onChange={(value) => {
                                dispatch(updateUserSettings({ data: { videoConversion: { audioCodec: value.name } }, isSaveSettings: true }))
                            }}
                        />
                        <Dropdown
                            containerClassName={cn(textStyles.center, styles.inputWidth, grid.pb_mini)}
                            label={translate('codecProfile')}
                            value={settingsVideoConversion.profile}
                            options={codecProfileOptions}
                            centered
                            onChange={(value) => {
                                dispatch(updateUserSettings({ data: { videoConversion: { profile: value.name } }, isSaveSettings: true }))
                            }}
                        />
                        <Dropdown
                            containerClassName={cn(textStyles.center, styles.inputWidth, grid.pb_mini)}
                            label={translate('codecLevel')}
                            value={settingsVideoConversion.level}
                            options={codecLevelOptions}
                            centered
                            onChange={(value) => {
                                dispatch(updateUserSettings({ data: { videoConversion: { level: value.name } }, isSaveSettings: true }))
                            }}
                        />
                        <Dropdown
                            containerClassName={cn(textStyles.center, styles.inputWidth, grid.pb_mini)}
                            label={translate('preset')}
                            value={settingsVideoConversion.preset}
                            options={codecPresetOptions}
                            centered
                            onChange={(value) => {
                                dispatch(updateUserSettings({ data: { videoConversion: { preset: value.name } }, isSaveSettings: true }))
                            }}
                        />
                        <div className={cn(grid.pb_mini, grid.row, grid.justify, styles.inputWidth, styles.greyText)}>
                            {translate('setForFfmpeg')}
                        </div>
                        <Dropdown
                            containerClassName={cn(textStyles.center, styles.inputWidth, grid.pb_mini)}
                            label={translate('crf')}
                            value={settingsVideoConversion.crf}
                            options={codecCrfOptions}
                            centered
                            onChange={(value) => {
                                dispatch(updateUserSettings({ data: { videoConversion: { crf: value.name } }, isSaveSettings: true }))
                            }}
                            dropDirection={'up'}
                        />
                    </div>)}
                    <div className={cn(grid.pt_mini, grid.row, grid.justify, styles.greyText)}>
                        {translate('choseOnePreset')}
                    </div>
                    <div className={cn(grid.row, grid.justify, styles.hint, styles.greyText)}>
                        {translate('presetAvaiableInVideoeditor')}
                    </div>
                </div>)}
            </div>
        </Collapse>
    )
}

export default DownloadVideoOptions
